import { ReactNode, useContext, useId } from 'react';
import { cn } from 'utils/cn';
import { isPlainLeftClick, PaletteContext } from './GlobalSearchRow';

export interface GlobalSearchSectionProps {
    /** The family's plural name, or the heading of a section that is not a family. */
    title: string;
    /** Everything the search found in this family, which is more than the rows below. */
    count?: number;
    /**
     * The one control in the heading: "see all" on a family, "clear" over recently viewed. It is
     * drawn as a link only when it has an `href` to go to — "clear" changes this list rather than
     * navigating, so a link would offer a destination that middle-clicking would then open.
     */
    action?: { label: string; href?: string; onSelect: () => void };
    /** A hairline above the heading, drawn on every section but the topmost; the palette spaces them instead. */
    divided?: boolean;
    /** The rows, which the caller builds so that a wrapped row still counts as one. */
    children: ReactNode;
}

const ACTION_CLASS =
    'text-link flex-none text-xs leading-[1.5] no-underline hover:underline max-sm:flex max-sm:min-h-11 max-sm:items-center';
const PALETTE_ACTION_CLASS = 'text-primary flex-none text-xs font-medium no-underline hover:underline';

/**
 * One group of rows under a heading that names the family and how many it found.
 *
 * The rows are passed in rather than derived from a list of results, so whatever wraps a row — the
 * hover peek does — drops into a section unchanged.
 */
export function GlobalSearchSection({ title, count, action, divided, children }: GlobalSearchSectionProps) {
    const headingId = useId();
    // The palette sets its sections apart by space alone, its rows inset from the card's edge.
    const palette = useContext(PaletteContext);
    const actionClass = palette ? PALETTE_ACTION_CLASS : ACTION_CLASS;

    return (
        <div
            role="group"
            aria-labelledby={headingId}
            className={
                palette ? 'flex flex-col gap-0.5 px-2 first:pt-1 last:pb-1' : cn(divided && 'border-grey-200 border-t')
            }
        >
            <div
                className={cn(
                    'flex items-center justify-between gap-3',
                    palette ? 'px-2.5 pt-3 pb-1' : 'px-3.5 pt-2.5 pb-1'
                )}
            >
                <span
                    id={headingId}
                    className={
                        palette
                            ? 'text-label text-grey-600 truncate uppercase'
                            : 'text-grey-500 truncate text-xs leading-[1.5] font-medium'
                    }
                >
                    {count === undefined ? title : `${title} · ${count}`}
                </span>
                {action !== undefined &&
                    // `max-sm` is the sheet and nothing else: a control tapped rather than clicked
                    // needs the same 44px the rows under it get.
                    (action.href === undefined ? (
                        <button type="button" className={actionClass} onClick={() => action.onSelect()}>
                            {action.label}
                        </button>
                    ) : (
                        <a
                            href={action.href}
                            className={actionClass}
                            onClick={(event) => {
                                if (!isPlainLeftClick(event)) {
                                    return;
                                }
                                event.preventDefault();
                                action.onSelect();
                            }}
                        >
                            {action.label}
                        </a>
                    ))}
            </div>
            {children}
        </div>
    );
}
